import "@/index.css";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, homeFor, useAuth } from "@/context/AuthContext";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import SuperAdmin from "@/pages/SuperAdmin";
import OrganizationAdmin from "@/pages/OrganizationAdmin";
import SchoolAdmin from "@/pages/SchoolAdmin";
import Teacher from "@/pages/Teacher";
import Parent from "@/pages/Parent";
import Student from "@/pages/Student";
import PrintAttendance from "@/pages/PrintAttendance";
import PrintTimetable from "@/pages/PrintTimetable";
import { PaymentCancel, PaymentSuccess } from "@/pages/PaymentResult";
import LegalPage from "@/components/LegalPage";

function LoadingScreen() {
  return (
    <div className="min-h-screen grid place-items-center bg-[#F4F5F7]">
      <div
        className="w-10 h-10 rounded-full border-4 border-brand-coral border-t-transparent animate-spin"
        data-testid="auth-loading"
      />
    </div>
  );
}

function Guard({ role, roles, children }) {
  const { user } = useAuth();

  if (user === undefined) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;

  const allowed = roles || (role ? [role] : null);

  if (allowed && !allowed.includes(user.role)) {
    return <Navigate to={homeFor(user)} replace />;
  }

  return children;
}

function OrganizationGuard({ children }) {
  const { user } = useAuth();

  if (user === undefined) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;

  const allowed =
    user.role === "school_admin" &&
    user.admin_scope === "organization" &&
    !user.access_context;

  if (!allowed) {
    return <Navigate to={homeFor(user)} replace />;
  }

  return children;
}

function SchoolAccessGuard({ children }) {
  const { user } = useAuth();

  if (user === undefined) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;

  const isBranchAdmin =
    user.role === "school_admin" &&
    user.admin_scope !== "organization";

  const isOrganizationBranchContext =
    user.role === "school_admin" &&
    user.admin_scope === "organization" &&
    Boolean(user.access_context);

  const isSuperAdminBranchContext =
    user.role === "super_admin" &&
    Boolean(user.access_context);

  if (
    !isBranchAdmin &&
    !isOrganizationBranchContext &&
    !isSuperAdminBranchContext
  ) {
    return <Navigate to={homeFor(user)} replace />;
  }

  return children;
}

function PrintAccessGuard({ children }) {
  const { user } = useAuth();

  if (user === undefined) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;

  if (user.role === "teacher") return children;

  const isBranchAdmin =
    user.role === "school_admin" &&
    user.admin_scope !== "organization";

  const isOrganizationBranchContext =
    user.role === "school_admin" &&
    user.admin_scope === "organization" &&
    Boolean(user.access_context);

  const isSuperAdminBranchContext =
    user.role === "super_admin" &&
    Boolean(user.access_context);

  if (
    !isBranchAdmin &&
    !isOrganizationBranchContext &&
    !isSuperAdminBranchContext
  ) {
    return <Navigate to={homeFor(user)} replace />;
  }

  return children;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          <Route
            path="/admin"
            element={
              <Guard role="super_admin">
                <SuperAdmin />
              </Guard>
            }
          />

          <Route
            path="/organization"
            element={
              <OrganizationGuard>
                <OrganizationAdmin />
              </OrganizationGuard>
            }
          />

          <Route
            path="/school"
            element={
              <SchoolAccessGuard>
                <SchoolAdmin />
              </SchoolAccessGuard>
            }
          />

          <Route
            path="/teacher"
            element={
              <Guard role="teacher">
                <Teacher />
              </Guard>
            }
          />

          <Route
            path="/parent"
            element={
              <Guard role="parent">
                <Parent />
              </Guard>
            }
          />

          <Route
            path="/student"
            element={
              <Guard role="student">
                <Student />
              </Guard>
            }
          />

          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />

          <Route
            path="/print/attendance"
            element={
              <PrintAccessGuard>
                <PrintAttendance />
              </PrintAccessGuard>
            }
          />

          <Route
            path="/print/timetable"
            element={
              <PrintAccessGuard>
                <PrintTimetable />
              </PrintAccessGuard>
            }
          />

          <Route path="/privacy" element={<LegalPage page="privacy" />} />
          <Route
            path="/data-protection"
            element={<LegalPage page="data-protection" />}
          />
          <Route path="/terms" element={<LegalPage page="terms" />} />
          <Route path="/cookies" element={<LegalPage page="cookies" />} />
          <Route
            path="/data-retention"
            element={<LegalPage page="data-retention" />}
          />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>

      <Toaster position="top-center" richColors />
    </AuthProvider>
  );
}

export default App;
